var __jsx = React.createElement;
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import useIsMobile from "@hooks/useIsMobile";
import { twMerge } from "tailwind-merge";
import { Conditional } from "@componentsShared/Conditional/Conditional";
import { Then } from "@componentsShared/Conditional/Then";
import { Else } from "@componentsShared/Conditional/Else";
import { Icon } from "@syarahtech/ui";
export function BnplOptions(_ref) {
  var _bnplOptions$, _bnplOptions$3, _bnplOptions$4, _bnplOptions$5, _bnplOptions$6, _bnplOptions$7;
  var bnplOptions = _ref.bnplOptions,
    onOptionClick = _ref.onOptionClick,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className;
  var _useTranslation = useTranslation(["web_post_view", "web_common"]),
    tShort = _useTranslation.tShort;
  var scrollContainerRef = useRef(null);
  var lang = useLang();
  var isRTL = lang === "ar";
  var isMobile = useIsMobile();
  var _useState = useState(isRTL),
    canScrollLeft = _useState[0],
    setCanScrollLeft = _useState[1];
  var _useState2 = useState(!isRTL),
    canScrollRight = _useState2[0],
    setCanScrollRight = _useState2[1];

  // Check scroll state
  var checkScrollState = function checkScrollState() {
    if (!scrollContainerRef.current) return;
    var _scrollContainerRef$c = scrollContainerRef.current,
      scrollLeft = _scrollContainerRef$c.scrollLeft,
      scrollWidth = _scrollContainerRef$c.scrollWidth,
      clientWidth = _scrollContainerRef$c.clientWidth;
    var maxScroll = scrollWidth - clientWidth;

    // No scroll needed if content fits
    if (maxScroll <= 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // For RTL, scrollLeft values are typically negative or work differently across browsers
    if (isRTL) {
      var normalizedScroll = Math.abs(scrollLeft);
      setCanScrollLeft(normalizedScroll < maxScroll - 1);
      setCanScrollRight(normalizedScroll > 1);
    } else {
      // LTR behavior
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(scrollLeft < maxScroll - 1);
    }
  };

  // Scroll functions - left button always scrolls left, right button always scrolls right
  var scrollLeft = function scrollLeft() {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: -200,
      behavior: "smooth"
    });
  };
  var scrollRight = function scrollRight() {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: 200,
      behavior: "smooth"
    });
  };

  // Set initial state when component mounts or direction changes
  useEffect(function () {
    if (!scrollContainerRef.current || !bnplOptions.length) return;
    var container = scrollContainerRef.current;
    var scrollWidth = container.scrollWidth,
      clientWidth = container.clientWidth;
    var hasScroll = scrollWidth > clientWidth;
    if (!hasScroll) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // Set initial visibility - show forward scroll button for both directions
    if (isRTL) {
      setCanScrollLeft(true);
      setCanScrollRight(false);
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(true);
    }

    // Both start at position 0
    container.scrollLeft = 0;
  }, [isRTL, bnplOptions]);

  // Add resize observer to check scroll state when container size changes
  useEffect(function () {
    var resizeObserver = new ResizeObserver(function () {
      if (scrollContainerRef.current) {
        var _scrollLeft = scrollContainerRef.current.scrollLeft;
        if (Math.abs(_scrollLeft) > 0) {
          checkScrollState();
        }
      }
    });
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }
    return function () {
      resizeObserver.disconnect();
    };
  }, []);
  if (!bnplOptions || bnplOptions.length === 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: twMerge("py-1 md:py-3", className)
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-[16px] text-[#00143C] font-semibold text-center md:text-start py-1 leading-5"
  }, tShort("post_details/financing_pay_later")), !isMobile ? /*#__PURE__*/React.createElement("div", {
    className: "mt-4 flex flex-col justify-center items-stretch gap-4 w-[345px] max-w-full"
  }, bnplOptions.map(function (option, idx) {
    return /*#__PURE__*/React.createElement("div", {
      key: option.type,
      onClick: function onClick() {
        return onOptionClick(option.type);
      },
      dir: isRTL ? "rtl" : "ltr",
      className: "flex flex-col gap-2 cursor-pointer group rounded-[8px] border border-solid border-[#E6E9EF] bg-[#F9FAFB] p-3"
    }, option.image_url && /*#__PURE__*/React.createElement("img", {
      src: option.image_url,
      alt: option.type,
      className: "h-9 w-auto object-contain self-start"
    }), /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-2"
    }, /*#__PURE__*/React.createElement("p", {
      className: "flex-1 text-[12px] font-semibold text-[#282A2F] leading-5"
    }, option.description), /*#__PURE__*/React.createElement(Icon, {
      icon: "material-symbols--info-outline",
      width: 4,
      height: 4,
      className: "text-[#0D0DB5]"
    })));
  })) : /*#__PURE__*/React.createElement("div", {
    className: "relative mt-4"
  }, canScrollLeft && !isMobile && bnplOptions.length > 2 && /*#__PURE__*/React.createElement("div", {
    onClick: scrollLeft,
    className: "cursor-pointer absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors [box-shadow:1px_2px_3.5px_1px_#00000021]",
    "aria-label": isRTL ? "Scroll forward" : "Scroll back"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "w-4 h-4 text-black",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M15 19l-7-7 7-7"
  }))), canScrollRight && !isMobile && bnplOptions.length > 2 && /*#__PURE__*/React.createElement("div", {
    onClick: scrollRight,
    className: "cursor-pointer absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors [box-shadow:1px_2px_3.5px_1px_#00000021]",
    "aria-label": isRTL ? "Scroll back" : "Scroll forward"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "w-4 h-4 text-black",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M9 5l7 7-7 7"
  }))), /*#__PURE__*/React.createElement("div", {
    ref: scrollContainerRef,
    dir: isRTL ? "rtl" : "ltr",
    className: twMerge("flex gap-3 overflow-x-auto pb-2", "[&::-webkit-scrollbar]:hidden", "[-ms-overflow-style:none]", "[scrollbar-width:none]", "px-0", bnplOptions.length <= 2 ? "justify-center" : ""),
    onScroll: checkScrollState
  }, /*#__PURE__*/React.createElement(Conditional, {
    condition: bnplOptions && bnplOptions.length == 1
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement("div", {
    className: "w-full flex items-center justify-center gap-2 p-3 border border-solid border-gray-gainsboro rounded-lg bg-white cursor-pointer",
    key: bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$ = bnplOptions[0]) === null || _bnplOptions$ === void 0 ? void 0 : _bnplOptions$.type,
    onClick: function onClick() {
      var _bnplOptions$2;
      return onOptionClick(bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$2 = bnplOptions[0]) === null || _bnplOptions$2 === void 0 ? void 0 : _bnplOptions$2.type);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center"
  }, (bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$3 = bnplOptions[0]) === null || _bnplOptions$3 === void 0 ? void 0 : _bnplOptions$3.image_url) && /*#__PURE__*/React.createElement("img", {
    src: bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$4 = bnplOptions[0]) === null || _bnplOptions$4 === void 0 ? void 0 : _bnplOptions$4.image_url,
    alt: bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$5 = bnplOptions[0]) === null || _bnplOptions$5 === void 0 ? void 0 : _bnplOptions$5.type,
    width: 65,
    height: 25,
    className: "max-w-full max-h-full object-contain"
  })), /*#__PURE__*/React.createElement("p", {
    className: " text-sm font-medium text-[#282A2F] leading-4 "
  }, bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$6 = bnplOptions[0]) === null || _bnplOptions$6 === void 0 ? void 0 : _bnplOptions$6.description, (bnplOptions === null || bnplOptions === void 0 ? void 0 : (_bnplOptions$7 = bnplOptions[0]) === null || _bnplOptions$7 === void 0 ? void 0 : _bnplOptions$7.type) === "tabby" && /*#__PURE__*/React.createElement("span", {
    className: "text-primary-new text-xs font-semibold underline ms-1"
  }, tShort("pre-checkout/how_to_tabby"))))), /*#__PURE__*/React.createElement(Else, null, bnplOptions.map(function (option) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      key: option.type,
      onClick: function onClick() {
        return onOptionClick(option.type);
      },
      className: "flex-shrink-0 w-44 md:w-40 h-28 bg-white border border-solid border-[#EFEFEF] rounded-lg px-2  py-3 cursor-pointer hover:border-primary-new transition-colors flex flex-col items-center justify-center text-center gap-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "h-8 flex items-center justify-center"
    }, option.image_url && /*#__PURE__*/React.createElement("img", {
      src: option.image_url,
      alt: option.type,
      className: "h-full w-auto object-contain"
    })), /*#__PURE__*/React.createElement("p", {
      className: " text-sm font-medium text-[#282A2F] leading-4 min-h-[3rem] "
    }, option.description, option.type === "tabby" && /*#__PURE__*/React.createElement("span", {
      className: "text-primary-new text-xs font-semibold underline ms-1"
    }, tShort("pre-checkout/how_to_tabby")))));
  }))))));
}
export default BnplOptions;